/**
 * MobileHelpSheet Component
 *
 * A bottom sheet overlay for mobile devices showing touch control help.
 * Features:
 * - Slide-up animation from bottom of screen
 * - Swipe-down to dismiss gesture
 * - Safe area inset support for notched devices
 * - Backdrop click to close
 *
 * @module components/HelpOverlay/MobileHelpSheet
 */

import React, { useRef, useEffect } from 'react';
import { MobileHelpSheetProps } from './types';
import { MOBILE_CONTROLS } from './constants';
import styles from './MobileHelpSheet.module.css';

/**
 * MobileHelpSheet displays touch control help in a bottom sheet format.
 *
 * @param props - Component props
 * @param props.isOpen - Whether the sheet is open
 * @param props.onClose - Callback when sheet should close
 *
 * @example
 * ```tsx
 * <MobileHelpSheet
 *   isOpen={showHelp}
 *   onClose={() => setShowHelp(false)}
 * />
 * ```
 */
export function MobileHelpSheet({ isOpen, onClose }: MobileHelpSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);

  // Reset transform when opening
  useEffect(() => {
    if (isOpen && sheetRef.current) {
      sheetRef.current.style.transform = '';
    }
  }, [isOpen]);

  // Handle touch start - record initial position
  const handleTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    currentY.current = e.touches[0].clientY;
  };

  // Handle touch move - translate sheet based on drag distance
  const handleTouchMove = (e: React.TouchEvent) => {
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    // Only allow dragging downward
    if (diff > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${diff}px)`;
    }
  };

  // Handle touch end - dismiss if dragged far enough, otherwise snap back
  const handleTouchEnd = () => {
    const diff = currentY.current - startY.current;

    if (diff > 100) {
      // Dismiss threshold reached
      onClose();
    } else if (sheetRef.current) {
      // Snap back to open position
      sheetRef.current.style.transform = '';
    }

    // Reset tracking values
    startY.current = 0;
    currentY.current = 0;
  };

  // Handle backdrop click
  const handleBackdropClick = () => {
    onClose();
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isOpen ? styles.visible : ''}`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${isOpen ? styles.open : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-help-title"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        <div className={styles.dragHandle} aria-hidden="true" />

        {/* Title */}
        <h2 id="mobile-help-title" className={styles.title}>
          Touch Controls
        </h2>

        {/* Control List */}
        <ul className={styles.controlList}>
          {MOBILE_CONTROLS.map((control) => (
            <li key={control.key} className={styles.controlItem}>
              <span className={styles.key}>{control.key}</span>
              <span className={styles.description}>{control.description}</span>
            </li>
          ))}
        </ul>

        {/* Close Button */}
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
        >
          Got it
        </button>
      </div>
    </>
  );
}

export default MobileHelpSheet;
